import type {
  AIAnalysisApplyActionResponse,
  BillingPlan,
  BillingStatus,
  BillingSubscription,
  Client,
  ColumnMapping,
  CommunicationAIAnalysis,
  ConversationThreadItem,
  DashboardData,
  EmailTemplate,
  GuidedImportCommitResponse,
  GuidedImportParseResponse,
  GuidedImportType,
  GuidedImportValidationResponse,
  Invoice,
  InvoiceAIAnalysesResponse,
  InvoiceThreadResponse,
  ManualNotePayload,
  MessageLog,
  MessageEventLog,
  NeedsReviewResponse,
  PendingAIAnalysesResponse,
  Policy,
  TemplateGenerationChannel,
  TemplateGenerationResponse,
  Tokens,
  WhatsAppTemplate,
  WorkspaceSettings,
  FicAuthUrlResponse,
  FicStatus,
  FicSyncResponse,
  FicSettingsUpdate
} from "../types/domain";
import { notifySessionExpired } from "./session";

const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000";

if (import.meta.env.PROD && !import.meta.env.VITE_API_BASE_URL) {
  throw new Error(
    "VITE_API_BASE_URL non è impostato. " +
    "Aggiungere la variabile d'ambiente prima di effettuare la build di produzione."
  );
}

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface RegisterResponse {
  message: string;
  email: string;
  verification_required: boolean;
  verification_sent_at: string;
}

export interface CsvImportResponse {
  created: number;
  skipped: number;
  errors?: string[];
}

export interface ParseGuidedImportPayload {
  import_type: GuidedImportType;
  file?: File;
  sheet_name?: string;
  import_session_id?: string;
}

async function request<T>(
  path: string,
  method: HttpMethod,
  token?: string,
  body?: unknown,
  isForm = false
): Promise<T> {
  const headers: Record<string, string> = {};
  if (!isForm) {
    headers["Content-Type"] = "application/json";
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    body: body ? (isForm ? (body as BodyInit) : JSON.stringify(body)) : undefined
  });

  if (!response.ok) {
    let detail = `Errore ${response.status}`;
    try {
      const payload = (await response.json()) as { detail?: string | Array<{ msg?: string; loc?: string[] }> };
      if (typeof payload.detail === "string") {
        detail = payload.detail;
      } else if (Array.isArray(payload.detail)) {
        detail = payload.detail.map((e) => e.msg ?? JSON.stringify(e)).join("; ");
      }
    } catch {
      // Ignore json parse error and keep default message.
    }

    if (token && response.status === 401) {
      notifySessionExpired();
    }

    throw new Error(detail);
  }

  if (response.status === 204) {
    return {} as T;
  }
  return (await response.json()) as T;
}

export async function login(email: string, password: string): Promise<Tokens> {
  return request<Tokens>("/auth/login", "POST", undefined, { email, password });
}

export async function register(payload: {
  email: string;
  password: string;
  full_name?: string;
  workspace_name: string;
  is_consultant?: boolean;
  consultant_invite_token?: string;
}): Promise<RegisterResponse> {
  return request<RegisterResponse>("/auth/register", "POST", undefined, payload);
}

export async function verifyEmail(payload: { email: string; token: string; otp: string }): Promise<{ message: string }> {
  return request<{ message: string }>("/auth/verify-email", "POST", undefined, payload);
}

export async function resendVerificationEmail(email: string): Promise<RegisterResponse> {
  return request<RegisterResponse>("/auth/resend-verification", "POST", undefined, { email });
}

export async function requestPasswordReset(email: string): Promise<{ message: string }> {
  return request<{ message: string }>("/auth/request-password-reset", "POST", undefined, { email });
}

export async function confirmPasswordReset(payload: {
  email: string;
  token: string;
  new_password: string;
}): Promise<{ message: string }> {
  return request<{ message: string }>("/auth/reset-password", "POST", undefined, payload);
}

export async function refreshWithWorkspace(
  refreshToken: string,
  workspaceId: string,
): Promise<Tokens> {
  return request<Tokens>("/auth/refresh", "POST", undefined, {
    refresh_token: refreshToken,
    workspace_id: workspaceId,
  });
}

export async function getDashboardToday(token: string): Promise<DashboardData> {
  return request<DashboardData>("/dashboard/today", "GET", token);
}

export async function getDashboardAIInsight(token: string): Promise<{ insight: string }> {
  return request<{ insight: string }>("/dashboard/ai-insight", "POST", token);
}

export async function listClients(token: string): Promise<Client[]> {
  const data = await request<{ items: Client[] }>("/clients?page=1&page_size=200", "GET", token);
  return data.items;
}

export async function createClient(
  token: string,
  payload: Pick<Client, "name" | "email" | "phone" | "preferred_channel"> & {
    status?: "active" | "paused" | "blocked";
    notes?: string;
  }
): Promise<Client> {
  return request<Client>("/clients", "POST", token, payload);
}

export async function updateClient(
  token: string,
  clientId: string,
  payload: Partial<Pick<Client, "name" | "email" | "phone" | "status" | "preferred_channel" | "notes">>
): Promise<Client> {
  return request<Client>(`/clients/${clientId}`, "PUT", token, payload);
}

export async function deleteClient(token: string, clientId: string): Promise<void> {
  await request<void>(`/clients/${clientId}`, "DELETE", token);
}

export async function importClientsCsv(token: string, file: File): Promise<CsvImportResponse> {
  const formData = new FormData();
  formData.append("file", file);
  return request<CsvImportResponse>("/clients/import", "POST", token, formData, true);
}

export async function listInvoices(token: string): Promise<Invoice[]> {
  const data = await request<{ items: Invoice[] }>("/invoices?page=1&page_size=200", "GET", token);
  return data.items;
}

export async function createInvoice(
  token: string,
  payload: {
    client_id: string;
    description: string;
    amount: number;
    due_date: string;
    invoice_ref: string;
    currency?: string;
    payment_link?: string;
  }
): Promise<Invoice> {
  return request<Invoice>("/invoices", "POST", token, payload);
}

export async function updateInvoice(
  token: string,
  invoiceId: string,
  payload: {
    client_id?: string;
    description?: string;
    amount?: number;
    due_date?: string;
    invoice_ref?: string;
    notes?: string;
    currency?: string;
    payment_link?: string;
  }
): Promise<Invoice> {
  return request<Invoice>(`/invoices/${invoiceId}`, "PUT", token, payload);
}

export async function deleteInvoice(token: string, invoiceId: string): Promise<void> {
  await request<void>(`/invoices/${invoiceId}`, "DELETE", token);
}

export async function importInvoicesCsv(token: string, file: File): Promise<CsvImportResponse> {
  const formData = new FormData();
  formData.append("file", file);
  return request<CsvImportResponse>("/invoices/import", "POST", token, formData, true);
}

export async function parseGuidedImport(
  token: string,
  payload: ParseGuidedImportPayload
): Promise<GuidedImportParseResponse> {
  const formData = new FormData();
  formData.append("import_type", payload.import_type);
  if (payload.file) {
    formData.append("file", payload.file);
  }
  if (payload.sheet_name) {
    formData.append("sheet_name", payload.sheet_name);
  }
  if (payload.import_session_id) {
    formData.append("import_session_id", payload.import_session_id);
  }
  return request<GuidedImportParseResponse>("/imports/parse", "POST", token, formData, true);
}

export async function validateGuidedImport(
  token: string,
  payload: { import_session_id: string; import_type: GuidedImportType; mapping: ColumnMapping }
): Promise<GuidedImportValidationResponse> {
  return request<GuidedImportValidationResponse>("/imports/validate", "POST", token, payload);
}

export async function commitGuidedImport(
  token: string,
  payload: {
    import_session_id: string;
    import_type: GuidedImportType;
    mapping: ColumnMapping;
    confirm_warnings: boolean;
  }
): Promise<GuidedImportCommitResponse> {
  return request<GuidedImportCommitResponse>("/imports/commit", "POST", token, payload);
}

export interface ImportJobStatus {
  id: string;
  import_type: string;
  status: string;
  total_rows: number;
  processed_rows: number;
  imported_count: number;
  skipped_count: number;
  error_count: number;
  error_message: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export async function getImportJob(token: string, jobId: string): Promise<ImportJobStatus> {
  return request<ImportJobStatus>(`/imports/jobs/${jobId}`, "GET", token);
}

export async function markInvoicePaid(
  token: string,
  invoiceId: string,
  paymentDate?: string,
  includeRelatedInstallments?: boolean
): Promise<Invoice> {
  return request<Invoice>(`/invoices/${invoiceId}/mark-paid`, "POST", token, {
    payment_date: paymentDate,
    include_related_installments: includeRelatedInstallments ?? false
  });
}

export async function promiseInvoice(token: string, invoiceId: string, promisedUntil: string): Promise<Invoice> {
  return request<Invoice>(`/invoices/${invoiceId}/promise`, "POST", token, {
    promised_until: promisedUntil
  });
}

export async function disputeInvoice(token: string, invoiceId: string, reason: string): Promise<Invoice> {
  return request<Invoice>(`/invoices/${invoiceId}/dispute`, "POST", token, { reason });
}

export async function listPolicies(token: string): Promise<Policy[]> {
  return request<Policy[]>("/policies", "GET", token);
}

export async function createPolicy(
  token: string,
  payload: {
    name: string;
    description?: string;
    is_active?: boolean;
    steps: Array<{ step_order: number; offset_days: number; channels: string; tone: string }>;
  }
): Promise<Policy> {
  return request<Policy>("/policies", "POST", token, payload);
}

export async function updatePolicy(
  token: string,
  policyId: string,
  payload: {
    name?: string;
    description?: string;
    is_active?: boolean;
    steps?: Array<{ step_order: number; offset_days: number; channels: string; tone: string }>;
  }
): Promise<Policy> {
  return request<Policy>(`/policies/${policyId}`, "PUT", token, payload);
}

export async function deletePolicy(token: string, policyId: string): Promise<void> {
  await request<void>(`/policies/${policyId}`, "DELETE", token);
}

export async function listEmailTemplates(token: string): Promise<EmailTemplate[]> {
  return request<EmailTemplate[]>("/templates/email", "GET", token);
}

export async function createEmailTemplate(
  token: string,
  payload: { name: string; subject: string; body: string; tone: string; language?: string }
): Promise<EmailTemplate> {
  return request<EmailTemplate>("/templates/email", "POST", token, payload);
}

export async function updateEmailTemplate(
  token: string,
  templateId: string,
  payload: { name?: string; subject?: string; body?: string; tone?: string; is_active?: boolean }
): Promise<EmailTemplate> {
  return request<EmailTemplate>(`/templates/email/${templateId}`, "PUT", token, payload);
}

export async function deleteEmailTemplate(token: string, templateId: string): Promise<void> {
  await request<void>(`/templates/email/${templateId}`, "DELETE", token);
}

export async function setEmailTemplateAsDefault(token: string, templateId: string): Promise<EmailTemplate> {
  return request<EmailTemplate>(`/templates/email/${templateId}/set-default`, "POST", token);
}

export async function listWhatsAppTemplates(token: string): Promise<WhatsAppTemplate[]> {
  return request<WhatsAppTemplate[]>("/templates/whatsapp", "GET", token);
}

export async function createWhatsAppTemplate(
  token: string,
  payload: { name: string; body: string; category?: string; language?: string; is_approved?: boolean }
): Promise<WhatsAppTemplate> {
  return request<WhatsAppTemplate>("/templates/whatsapp", "POST", token, payload);
}

export async function updateWhatsAppTemplate(
  token: string,
  templateId: string,
  payload: { name?: string; body?: string; is_active?: boolean; is_user_approved?: boolean }
): Promise<WhatsAppTemplate> {
  return request<WhatsAppTemplate>(`/templates/whatsapp/${templateId}`, "PUT", token, payload);
}

export async function deleteWhatsAppTemplate(token: string, templateId: string): Promise<void> {
  await request<void>(`/templates/whatsapp/${templateId}`, "DELETE", token);
}

export async function setWhatsAppTemplateAsDefault(token: string, templateId: string): Promise<WhatsAppTemplate> {
  return request<WhatsAppTemplate>(`/templates/whatsapp/${templateId}/set-default`, "POST", token);
}

export async function submitWhatsAppTemplateToMeta(
  token: string,
  templateId: string,
  exampleValues?: string[],
): Promise<{ id: string; meta_template_id: string; meta_status: string }> {
  return request(`/templates/whatsapp/${templateId}/submit-to-meta`, "POST", token, {
    example_values: exampleValues ?? null,
  });
}

export async function checkWhatsAppTemplateStatus(token: string, templateId: string): Promise<WhatsAppTemplate> {
  return request<WhatsAppTemplate>(`/templates/whatsapp/${templateId}/check-status`, "POST", token);
}

export async function deleteWhatsAppTemplateFromMeta(token: string, templateId: string): Promise<void> {
  await request<void>(`/templates/whatsapp/${templateId}/meta`, "DELETE", token);
}

export async function generateTemplateSuggestions(
  token: string,
  payload: {
    channel: TemplateGenerationChannel;
    language?: string;
    model?: string;
    context?: string;
  }
): Promise<TemplateGenerationResponse> {
  return request<TemplateGenerationResponse>("/templates/generate", "POST", token, payload);
}

export async function listMessages(token: string): Promise<MessageLog[]> {
  const data = await request<{ items: MessageLog[] }>("/messages?page=1&page_size=200", "GET", token);
  return data.items;
}

export async function listMessageEvents(token: string, messageId: string): Promise<MessageEventLog[]> {
  const data = await request<{ items: MessageEventLog[] }>(`/messages/${messageId}/events`, "GET", token);
  return data.items;
}

export async function sendManual(
  token: string,
  payload: { invoice_id: string; channel: "email" | "whatsapp"; tone?: string; force?: boolean }
): Promise<{ message_id: string; status: string }> {
  return request<{ message_id: string; status: string }>("/send/manual", "POST", token, {
    tone: "standard",
    ...payload
  });
}

export async function previewMessage(
  token: string,
  payload: { invoice_id: string; channel: "email" | "whatsapp"; tone?: string; use_ai?: boolean }
): Promise<{ subject?: string; body: string; fallback_reason?: string; used_ai: boolean }> {
  return request<{ subject?: string; body: string; fallback_reason?: string; used_ai: boolean }>(
    "/send/preview",
    "POST",
    token,
    {
      tone: "standard",
      use_ai: true,
      ...payload
    }
  );
}

export async function getWorkspaceSettings(token: string): Promise<WorkspaceSettings> {
  return request<WorkspaceSettings>("/workspace/settings", "GET", token);
}

export async function updateWorkspaceSettings(
  token: string,
  payload: Partial<WorkspaceSettings> & {
    smtp_password?: string;
    smtp_password_clear?: boolean;
    whatsapp_access_token?: string;
    whatsapp_access_token_clear?: boolean;
  }
): Promise<WorkspaceSettings> {
  return request<WorkspaceSettings>("/workspace/settings", "PUT", token, payload);
}

// ─── Reports ──────────────────────────────────────────────────────────────

export async function downloadRecoveryReport(token: string, year: number, month: number): Promise<Blob> {
  const res = await fetch(`${API_BASE_URL}/api/reports/recovery-pdf?year=${year}&month=${month}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Errore ${res.status}`);
  }
  return res.blob();
}

// ─── Demo Data ─────────────────────────────────────────────────────────────

export async function seedDemoData(
  token: string,
  force = false
): Promise<{ clients: number; invoices: number; templates: number; policies: number }> {
  return request<{ clients: number; invoices: number; templates: number; policies: number }>(
    `/workspace/demo-data/seed?force=${force}`,
    "POST",
    token
  );
}

export async function clearDemoData(
  token: string
): Promise<Record<string, number>> {
  return request<Record<string, number>>("/workspace/demo-data", "DELETE", token);
}

// ─── Billing ───────────────────────────────────────────────────────────────

export async function listBillingPlans(token: string): Promise<BillingPlan[]> {
  return request<BillingPlan[]>("/billing/plans", "GET", token);
}

export async function getBillingStatus(token: string): Promise<BillingStatus> {
  return request<BillingStatus>("/billing/current", "GET", token);
}

export async function selectBillingPlan(
  token: string,
  planCode: string
): Promise<{ subscription: BillingSubscription }> {
  return request<{ subscription: BillingSubscription }>("/billing/select-plan", "POST", token, {
    plan_code: planCode
  });
}

export async function createBillingPaymentIntent(
  token: string,
  payload: { plan_code?: string; subscription_id?: string }
): Promise<{
  subscription: BillingSubscription;
  payment_intent_id: string;
  client_secret: string;
  publishable_key: string;
}> {
  return request<{
    subscription: BillingSubscription;
    payment_intent_id: string;
    client_secret: string;
    publishable_key: string;
  }>("/billing/payment-intent", "POST", token, payload);
}

export async function refreshBillingSubscription(
  token: string,
  payload: { subscription_id?: string }
): Promise<{
  workspace_status: string;
  has_active_subscription: boolean;
  subscription?: BillingSubscription | null;
}> {
  return request<{
    workspace_status: string;
    has_active_subscription: boolean;
    subscription?: BillingSubscription | null;
  }>("/billing/refresh", "POST", token, payload);
}

export async function changeBillingPlan(
  token: string,
  planCode: string
): Promise<{ subscription: BillingSubscription }> {
  return request<{ subscription: BillingSubscription }>("/billing/change-plan", "POST", token, {
    plan_code: planCode
  });
}

export async function cancelBillingSubscription(
  token: string,
  payload: { subscription_id?: string } = {}
): Promise<{
  message: string;
  subscription?: BillingSubscription | null;
  workspace_status: string;
}> {
  return request<{
    message: string;
    subscription?: BillingSubscription | null;
    workspace_status: string;
  }>("/billing/cancel", "POST", token, payload);
}

export async function createBillingPortalSession(
  token: string,
  payload: { return_url?: string } = {}
): Promise<{ url: string }> {
  return request<{ url: string }>("/billing/portal-session", "POST", token, payload);
}

export async function syncWhatsAppTemplates(token: string): Promise<{ synced_templates: number }> {
  return request<{ synced_templates: number }>("/integrations/whatsapp/templates/sync", "POST", token);
}

export async function testWhatsAppIntegration(
  token: string,
  payload: {
    provider_name: "meta_cloud";
    phone_number_id?: string;
    business_account_id?: string;
    access_token?: string;
    api_version?: string;
  }
): Promise<{
  connected: boolean;
  provider_name: string;
  message: string;
  display_phone_number?: string;
  verified_name?: string;
}> {
  return request<{
    connected: boolean;
    provider_name: string;
    message: string;
    display_phone_number?: string;
    verified_name?: string;
  }>("/integrations/whatsapp/connect", "POST", token, payload);
}

// ─── Conversations ───────────────────────────────────────────────────────────

export async function getInvoiceThread(
  token: string,
  invoiceId: string,
  channel?: string,
  direction?: string
): Promise<InvoiceThreadResponse> {
  const params = new URLSearchParams();
  if (channel) params.set("channel", channel);
  if (direction) params.set("direction", direction);
  const qs = params.toString() ? `?${params.toString()}` : "";
  return request<InvoiceThreadResponse>(`/conversations/invoice/${invoiceId}/thread${qs}`, "GET", token);
}

export async function createManualNote(
  token: string,
  invoiceId: string,
  payload: ManualNotePayload
): Promise<ConversationThreadItem> {
  return request<ConversationThreadItem>(`/conversations/invoice/${invoiceId}/notes`, "POST", token, payload);
}

export async function listNeedsReview(token: string, page = 1): Promise<NeedsReviewResponse> {
  return request<NeedsReviewResponse>(`/conversations/review?page=${page}&page_size=50`, "GET", token);
}

export async function assignConversationMessage(
  token: string,
  messageId: string,
  invoiceId: string
): Promise<{ id: string; invoice_id: string; matching_status: string; thread_id: string | null }> {
  return request<{ id: string; invoice_id: string; matching_status: string; thread_id: string | null }>(
    `/conversations/messages/${messageId}/assign`,
    "PUT",
    token,
    { invoice_id: invoiceId }
  );
}

// ─── AI Analysis ─────────────────────────────────────────────────────────────

export async function getMessageAIAnalysis(
  token: string,
  messageId: string
): Promise<CommunicationAIAnalysis> {
  return request<CommunicationAIAnalysis>(
    `/conversations/messages/${messageId}/ai-analysis`,
    "GET",
    token
  );
}

export async function reanalyzeMessage(
  token: string,
  messageId: string
): Promise<{ conversation_message_id: string; status: string; message: string }> {
  return request<{ conversation_message_id: string; status: string; message: string }>(
    `/conversations/messages/${messageId}/ai-analysis/reanalyze`,
    "POST",
    token
  );
}

export async function getInvoiceAIAnalyses(
  token: string,
  invoiceId: string
): Promise<InvoiceAIAnalysesResponse> {
  return request<InvoiceAIAnalysesResponse>(
    `/conversations/invoice/${invoiceId}/ai-analyses`,
    "GET",
    token
  );
}

export async function listPendingAIAnalyses(
  token: string,
  channel?: string
): Promise<PendingAIAnalysesResponse> {
  const params = channel ? `?channel=${channel}` : "";
  return request<PendingAIAnalysesResponse>(
    `/conversations/ai-analyses/pending${params}`,
    "GET",
    token
  );
}

export async function confirmAIAnalysis(
  token: string,
  messageId: string
): Promise<CommunicationAIAnalysis> {
  return request<CommunicationAIAnalysis>(
    `/conversations/messages/${messageId}/ai-analysis/confirm`,
    "POST",
    token,
    {}
  );
}

export async function correctAIAnalysis(
  token: string,
  messageId: string,
  payload: { corrected_classification?: string; corrected_action_code?: string }
): Promise<CommunicationAIAnalysis> {
  return request<CommunicationAIAnalysis>(
    `/conversations/messages/${messageId}/ai-analysis/correct`,
    "POST",
    token,
    payload
  );
}

export async function applyAIAction(
  token: string,
  messageId: string,
  payload: { action_code?: string; promised_payment_date?: string; notes?: string }
): Promise<AIAnalysisApplyActionResponse> {
  return request<AIAnalysisApplyActionResponse>(
    `/conversations/messages/${messageId}/ai-analysis/apply-action`,
    "POST",
    token,
    payload
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export async function getNotificationCounts(
  token: string
): Promise<{ pending_ai_analyses: number; needs_review_messages: number; total: number }> {
  return request<{ pending_ai_analyses: number; needs_review_messages: number; total: number }>(
    "/dashboard/notifications",
    "GET",
    token
  );
}

// ─── Notifications ──────────────────────────────────────────────────────────

export interface NotificationItem {
  id: string;
  event_type: string;
  title: string;
  body: string;
  link: string | null;
  entity_type: string | null;
  entity_id: string | null;
  channel_origin: string | null;
  status: string;
  created_at: string;
  read_at: string | null;
}

export async function listNotifications(
  token: string,
  params: { status?: string; limit?: number; offset?: number } = {}
): Promise<{ items: NotificationItem[]; total: number; unread_count: number }> {
  const qs = new URLSearchParams();
  if (params.status) qs.set("status", params.status);
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.offset) qs.set("offset", String(params.offset));
  const suffix = qs.toString() ? `?${qs}` : "";
  return request(`/notifications${suffix}`, "GET", token);
}

export async function getUnreadCount(
  token: string
): Promise<{ unread_count: number; by_type: Record<string, number> }> {
  return request("/notifications/unread-count", "GET", token);
}

export async function markNotificationRead(token: string, id: string): Promise<void> {
  await request(`/notifications/${id}/read`, "POST", token);
}

export async function markAllNotificationsRead(token: string): Promise<void> {
  await request("/notifications/read-all", "POST", token);
}

export async function getNotificationPreferences(
  token: string
): Promise<{ preferences: { event_type: string; channel: string; enabled: boolean }[] }> {
  return request("/notifications/preferences", "GET", token);
}

export async function updateNotificationPreferences(
  token: string,
  preferences: { event_type: string; channel: string; enabled: boolean }[]
): Promise<{ preferences: { event_type: string; channel: string; enabled: boolean }[] }> {
  return request("/notifications/preferences", "PUT", token, { preferences });
}

export async function getPushPublicKey(token: string): Promise<{ public_key: string }> {
  return request("/notifications/push-public-key", "GET", token);
}

export async function registerPushSubscription(
  token: string,
  sub: { endpoint: string; p256dh_key: string; auth_key: string; user_agent?: string }
): Promise<void> {
  await request("/notifications/push-subscription", "POST", token, sub);
}

// ─── User Profile ───────────────────────────────────────────────────────────

export interface UserProfile {
  id: string;
  email: string;
  full_name: string | null;
  is_email_verified: boolean;
}

export async function getMyProfile(token: string): Promise<UserProfile> {
  return request("/auth/me", "GET", token);
}

export async function updateMyProfile(
  token: string,
  payload: { full_name: string | null },
): Promise<UserProfile> {
  return request("/auth/me", "PUT", token, payload);
}

export async function changePassword(
  token: string,
  payload: { current_password: string; new_password: string },
): Promise<{ message: string }> {
  return request("/auth/change-password", "POST", token, payload);
}

// ─── Integrations ───────────────────────────────────────────────────────────

export async function testEmailIntegration(
  token: string,
  payload: {
    to: string;
    smtp_host?: string;
    smtp_port?: number;
    smtp_username?: string;
    smtp_password?: string;
    smtp_use_tls?: boolean;
    smtp_use_ssl?: boolean;
    from_email?: string;
  }
): Promise<{ success: boolean; message: string }> {
  return request<{ success: boolean; message: string }>("/integrations/email/test", "POST", token, payload);
}

// ──────────────────────────────────────────────────────────────────────────────
// Admin API
// ──────────────────────────────────────────────────────────────────────────────

export function adminDashboardStats(token: string) {
  return request<any>("/admin/dashboard/stats", "GET", token);
}

export function adminListWorkspaces(token: string, params?: Record<string, string>) {
  const qs = params ? "?" + new URLSearchParams(params).toString() : "";
  return request<any>(`/admin/workspaces${qs}`, "GET", token);
}

export function adminGetWorkspace(token: string, id: string) {
  return request<any>(`/admin/workspaces/${id}`, "GET", token);
}

export function adminUpdateWorkspace(token: string, id: string, payload: Record<string, any>) {
  return request<any>(`/admin/workspaces/${id}`, "PUT", token, payload);
}

export function adminListUsers(token: string, params?: Record<string, string>) {
  const qs = params ? "?" + new URLSearchParams(params).toString() : "";
  return request<any>(`/admin/users${qs}`, "GET", token);
}

export function adminGetUser(token: string, id: string) {
  return request<any>(`/admin/users/${id}`, "GET", token);
}

export function adminUpdateUser(token: string, id: string, payload: Record<string, any>) {
  return request<any>(`/admin/users/${id}`, "PUT", token, payload);
}

export function adminResetPassword(token: string, userId: string) {
  return request<{ reset_link: string }>(`/admin/users/${userId}/reset-password`, "POST", token);
}

export function adminListSubscriptions(token: string, workspaceId: string) {
  return request<any>(`/admin/workspaces/${workspaceId}/subscriptions`, "GET", token);
}

export function adminActivateSubscription(token: string, workspaceId: string, payload: { plan_code: string; duration_days?: number }) {
  return request<any>(`/admin/workspaces/${workspaceId}/subscriptions/activate`, "POST", token, payload);
}

export function adminExtendSubscription(token: string, workspaceId: string, payload: { days: number }) {
  return request<any>(`/admin/workspaces/${workspaceId}/subscriptions/extend`, "POST", token, payload);
}

export function adminCancelSubscription(token: string, workspaceId: string) {
  return request<any>(`/admin/workspaces/${workspaceId}/subscriptions/cancel`, "POST", token);
}

export function adminListAuditLogs(token: string, params?: Record<string, string>) {
  const qs = params ? "?" + new URLSearchParams(params).toString() : "";
  return request<any>(`/admin/audit-logs${qs}`, "GET", token);
}

export function adminListConversations(token: string, params?: Record<string, string>) {
  const qs = params ? "?" + new URLSearchParams(params).toString() : "";
  return request<any>(`/admin/conversations${qs}`, "GET", token);
}

export function adminGetConversationMessages(token: string, threadId: string) {
  return request<any>(`/admin/conversations/${threadId}/messages`, "GET", token);
}

export function adminImpersonate(token: string, workspaceId: string) {
  return request<{ access_token: string; workspace_id: string; workspace_name: string; expires_minutes: number }>(
    `/admin/impersonate/${workspaceId}`, "POST", token
  );
}

export function adminListContactRequests(token: string, params?: Record<string, string>) {
  const qs = params ? "?" + new URLSearchParams(params).toString() : "";
  return request<any>(`/admin/contact-requests${qs}`, "GET", token);
}

export function adminUpdateContactRequest(token: string, id: string, payload: Record<string, any>) {
  return request<any>(`/admin/contact-requests/${id}`, "PUT", token, payload);
}

export function adminReplyContactRequest(token: string, id: string, payload: { message: string }) {
  return request<any>(`/admin/contact-requests/${id}/reply`, "POST", token, payload);
}

// --- Admin Workspace Health ---

export function adminGetWorkspaceHealth(token: string, params?: Record<string, string>) {
  const qs = params ? "?" + new URLSearchParams(params).toString() : "";
  return request<any>(`/admin/workspace-health${qs}`, "GET", token);
}

// --- Admin Plans ---

export function adminListPlans(token: string) {
  return request<any[]>("/admin/plans", "GET", token);
}

export function adminCreatePlan(token: string, payload: Record<string, any>) {
  return request<any>("/admin/plans", "POST", token, payload);
}

export function adminUpdatePlan(token: string, planId: string, payload: Record<string, any>) {
  return request<any>(`/admin/plans/${planId}`, "PUT", token, payload);
}

export function adminDeletePlan(token: string, planId: string) {
  return request<void>(`/admin/plans/${planId}`, "DELETE", token);
}

// --- Fatture in Cloud Integration ---

export function ficGetAuthUrl(token: string) {
  return request<FicAuthUrlResponse>("/integrations/fic/auth-url", "GET", token);
}

export function ficGetStatus(token: string) {
  return request<FicStatus>("/integrations/fic/status", "GET", token);
}

export function ficSync(token: string) {
  return request<FicSyncResponse>("/integrations/fic/sync", "POST", token);
}

export function ficDisconnect(token: string) {
  return request<{ success: boolean; message: string }>("/integrations/fic/disconnect", "DELETE", token);
}

export function ficUpdateSettings(token: string, payload: FicSettingsUpdate) {
  return request<FicStatus>("/integrations/fic/settings", "PUT", token, payload);
}

// --- Aruba Fatturazione Integration ---

export function arubaConnect(token: string, payload: { username: string; password: string }) {
  return request<{ success: boolean; message: string }>("/integrations/aruba/connect", "POST", token, payload);
}

export function arubaGetStatus(token: string) {
  return request<{
    connected: boolean;
    status: string;
    api_username: string | null;
    last_sync_at: string | null;
    last_sync_error: string | null;
    sync_invoices: boolean;
    sync_interval_minutes: number;
  }>("/integrations/aruba/status", "GET", token);
}

export function arubaSync(token: string) {
  return request<{ success: boolean; message: string; invoices_created: number; invoices_updated: number; invoices_skipped: number }>("/integrations/aruba/sync", "POST", token);
}

export function arubaDisconnect(token: string) {
  return request<{ success: boolean; message: string }>("/integrations/aruba/disconnect", "DELETE", token);
}

export function arubaUpdateSettings(token: string, payload: { sync_invoices?: boolean; sync_interval_minutes?: number }) {
  return request<{ connected: boolean; status: string; sync_invoices: boolean; sync_interval_minutes: number }>("/integrations/aruba/settings", "PUT", token, payload);
}

// ─── Studio (Commercialisti) ─────────────────────────────────────────────────

export async function getStudioDashboard(token: string) {
  return request<any>("/studio/dashboard", "GET", token);
}

export async function getStudioInvitations(token: string, status?: string) {
  const params = status ? `?status=${status}` : "";
  return request<any>(`/studio/invitations${params}`, "GET", token);
}

export async function createStudioInvitation(
  token: string,
  data: { client_email: string; client_company_name?: string }
) {
  return request<any>("/studio/invitations", "POST", token, data);
}

export async function revokeStudioInvitation(token: string, id: string) {
  return request<void>(`/studio/invitations/${id}`, "DELETE", token);
}

export async function inviteConsultant(token: string, consultant_email: string) {
  return request<any>("/workspace/invite-consultant", "POST", token, { consultant_email });
}

export async function resendStudioInvitation(token: string, id: string) {
  return request<any>(`/studio/invitations/${id}/resend`, "POST", token);
}

export async function getStudioProfile(token: string) {
  return request<any>("/studio/profile", "GET", token);
}

export async function updateStudioProfile(token: string, studio_name: string) {
  return request<any>("/studio/profile", "PATCH", token, { studio_name });
}

export async function downloadClientReport(token: string, workspaceId: string): Promise<Blob> {
  const res = await fetch(`${API_BASE_URL}/studio/clients/${workspaceId}/report`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Errore download report");
  return res.blob();
}

export async function exportClientsCsv(token: string): Promise<Blob> {
  const res = await fetch(`${API_BASE_URL}/studio/clients/export`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Errore export CSV");
  return res.blob();
}

// --- Workspace Members ---

export async function inviteWorkspaceMember(
  token: string,
  email: string,
  role: "viewer" | "operator" | "admin" = "viewer"
): Promise<{ invited_user_id: string; workspace_user_id: string; role: string; message: string }> {
  const resp = await fetch(`${API_BASE_URL}/workspace/users/invite`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ email, role }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ detail: "Errore durante l'invito" }));
    throw new Error(err.detail || `Errore ${resp.status}`);
  }
  return resp.json();
}

// --- Danea EasyFatt Integration ---

export async function daneaImport(token: string, file: File) {
  const formData = new FormData();
  formData.append("file", file);
  const resp = await fetch(`${API_BASE_URL}/integrations/danea/import`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ detail: "Errore durante l'import" }));
    throw new Error(err.detail || `Errore ${resp.status}`);
  }
  return resp.json() as Promise<{ success: boolean; message: string; clients_created: number; clients_existing: number; invoices_created: number; invoices_skipped: number }>;
}
